import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getAllLots } from "../api/ApiRoutesFile";

export const lotApiSlice = createApi({
  reducerPath: "lotApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "https://castle-auction-production.up.railway.app/api/",
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      const token = localStorage.getItem("auction_portal");
      if (token) {
        headers.set("x-auth-token", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Lots"],
  endpoints: (builder) => ({
    lots: builder.query({
      query: ({ endpoint, id, params, search }) => {
        console.log('Lot API query params:', { endpoint, id, params, search });
        
        let url = id ? `${endpoint}${id}` : endpoint;
        
        // Build query parameters
        const queryParams = new URLSearchParams();
        
        // Add existing params if they exist
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              queryParams.append(key, value);
            }
          });
        }
        
        // Add search parameter if provided
        if (search && search.trim() !== '') {
          queryParams.append('search', search.trim());
          console.log('Search parameter added to lot query:', search.trim());
        }
        
        // Append query string if we have parameters
        if (queryParams.toString()) {
          url += `?${queryParams.toString()}`;
        }
        
        console.log('Final lot API URL:', url);
        
        return {
          url,
          method: "GET",
        };
      },
      // Disable caching for search results to ensure fresh data
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      // Add search-specific cache invalidation
      serializeQueryArgs: ({ queryArgs }) => {
        const { endpoint, id, params, search } = queryArgs;
        return `${endpoint}${id || ""}${JSON.stringify(params) || ""}${search || ""}`;
      },
      providesTags: (result, error, { params, search }) => {
        const tags = [{ type: "Lots", id: `LIST${JSON.stringify(params) || ""}${search || ""}` }];
        
        if (search && search.trim()) {
          tags.push({ type: "Lots", id: `SEARCH-${search.trim()}` });
        }
        
        if (!result || !result.lots) {
          return tags;
        }
        
        return [
          ...result.lots.map(({ _id }) => ({
            type: "Lots",
            id: _id,
          })),
          ...tags,
        ];
      },
    }),

    createLot: builder.mutation({
      query: ({ endpoint, formData }) => ({
        url: endpoint,
        method: "POST",
        body: formData,
      }),
      // Wait for the API response before updating the cache
      async onQueryStarted(
        { endpoint, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled; // wait for the real API response
          if (data && data.lot && data.lot._id) {
            // Update the paginated query cache (if using a page-based cache)
            dispatch(
              lotApiSlice.util.updateQueryData(
                "lots",
                { endpoint: getAllLots, id: lastId },
                (draft) => {
                  // Insert the new lot at the beginning (top)
                  draft.lots.unshift(data.lot);
                }
              )
            );
          }
        } catch (error) {
          console.error("Create failed:", error);
        }
      },
    }),

    updateLot: builder.mutation({
      query: ({ endpoint, id, formData }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT",
        body: formData,
      }),
      // Wait for API response before updating the cache
      async onQueryStarted(
        { endpoint, id, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled; // wait for the API call to complete
          if (data && data.lot) {
            // Update paginated cache if applicable
            dispatch(
              lotApiSlice.util.updateQueryData(
                "lots",
                { endpoint: getAllLots, id: lastId },
                (draft) => {
                  const index = draft.lots.findIndex((a) => a._id === id);
                  if (index !== -1) {
                    draft.lots[index] = data.lot;
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Update failed:", error);
          // Handle rollback if needed
        }
      },
    }),

    deleteLot: builder.mutation({
      query: ({ endpoint, id }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT", // Change to "DELETE" if your backend supports it
      }),

      // Update cache only after API confirms deletion
      async onQueryStarted({ endpoint, id }, { dispatch, queryFulfilled }) {
        try {
          // Wait for successful API response
          const { data } = await queryFulfilled;

          if (data && data?.lot) {
            dispatch(
              lotApiSlice.util.updateQueryData(
                "lots",
                { endpoint: getAllLots, id: 1 }, // Replace `1` with dynamic lastId if needed
                (draft) => {
                  if (draft && draft.lots) {
                    draft.lots = draft.lots.filter((item) => item._id !== id);
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Delete lot failed:", error);
        }
      },
    }),
  }),
});

export const {
  useLotsQuery,
  useCreateLotMutation,
  useUpdateLotMutation,
  useDeleteLotMutation,
} = lotApiSlice;
